import type { ServiceRecord } from "../index";

export const service: ServiceRecord = {
  slug: "cross-domain-data-transfer",
  kind: "domain-vertical",
  title: "Cross-Domain Data Transfer",
  shortTitle: "Cross-Domain Data",
  status: "draft",
  tier: "experience",
  seoTitle: "Cross-Domain Data Transfer | General Applications",
  seoDescription:
    "Unidirectional security guards, schema validation, and multi-level data sanitization for transferring information across classified enclaves.",
  summary:
    "Data diodes, guard validation, schema sanitization, and automated inspection pipelines for transferring mission data safely across classified, operational, and unclassified enclave boundaries.",
  image: "/images/blog/shipping-s1000d-offline-hero.webp",
  relatedCapabilitySlugs: [],
  relatedIndustrySlugs: ["military"],
  relatedPostSlugs: [
    "shipping-s1000d-offline",
    "replicating-without-a-database",
  ],
  engineering: [
    "Unidirectional and guarded data-transfer patterns across security boundaries",
    "Source-to-destination data contracts, classification, and handling rules",
    "Schema validation and field-level sanitization before delivery",
    "Explicit allow, reject, quarantine, and operator-review paths",
    "Transfer audit records that retain source, decision, and destination context",
    "Offline and disconnected delivery-package workflows",
  ],
  body: [
    {
      type: "heading",
      level: 2,
      text: "A Transfer Is a Security Boundary",
    },
    {
      type: "paragraph",
      text: "Moving information between differently trusted environments is not ordinary integration with an additional approval step. The path, direction, allowed data shape, handling decisions, and failure behavior are part of the system's boundary. A usable design makes those decisions explicit before data is allowed to move.",
    },
    {
      type: "heading",
      level: 2,
      text: "Design the Exchange, Not Just the Connection",
    },
    {
      type: "paragraph",
      text: "The useful unit of work is a controlled exchange: a known source, a permitted destination, a defined data contract, a validation decision, and a record of what happened. That keeps a rejected or incomplete transfer visible for review instead of allowing it to become an unexplained gap downstream.",
    },
  ],
  phases: {
    study: {
      focus:
        "Establish the actual source, destination, data types, trust boundary, and operational authority before selecting a transfer mechanism.",
      produces: [
        "Boundary, source, destination, and data-flow inventory",
        "Data classification, handling, and transfer-direction model",
        "Transfer-constraint and failure-mode analysis",
        "Authority, review, and exception-ownership map",
      ],
      fit: {
        good: [
          "Separate environments that need deliberate, traceable movement of defined information",
          "Teams replacing manual export, hand-carry, or re-entry work with a controlled exchange",
        ],
        poor: [
          "General file sharing inside a single trust boundary",
          "A transfer request whose source, destination, or approving authority is not yet known",
        ],
      },
    },
    "initial-design": {
      focus:
        "Define the exchange boundary, data contract, validation decisions, and accountability model as one design.",
      produces: [
        "Source-to-destination transfer architecture",
        "Canonical message, file, and metadata contract",
        "Allowed, rejected, quarantined, and reviewable outcome model",
        "Audit-trace and operational-ownership design",
      ],
    },
    "detailed-design": {
      focus:
        "Specify the checks, sanitization rules, exception paths, and deployment procedures that make the boundary operable.",
      produces: [
        "Validation and field-handling specifications",
        "Rejection, quarantine, retry, and human-review procedures",
        "Transfer logging and reconciliation requirements",
        "Disconnected-delivery and recovery procedures",
      ],
    },
    implementation: {
      focus:
        "Build and verify the exchange around the real systems that create, inspect, receive, and account for the transferred information.",
      produces: [
        "Controlled transfer workflow with explicit validation outcomes",
        "Traceable records of accepted, rejected, and reviewed transfers",
        "Operational runbooks for exception handling and recovery",
        "Verification evidence connecting the transfer contract to the deployed boundary",
      ],
    },
  },
};
